/* eslint-disable react/jsx-props-no-spreading */

import {
  Redirect, Route, makeRouteConfig,
} from 'found';
import { DateTime } from 'luxon';
import React from 'react';
import { graphql } from 'react-relay';

import { handleApplicationErrors } from 'src/auth/errorHandler';
// Admin
import {
  AdminCommunityList,
  AdminCommunityShow,
  AdminMeterList,
  AdminMeterShow,
  AdminMeterTradeRules,
  AdminPortfolioCreate,
  AdminPortfolioEdit,
  AdminPortfolioList,
  AdminPortfolioPropertyMemberCreate,
  AdminPortfolioPropertyMemberRetire,
  AdminPortfolioPropertyMemberShow,
  AdminPortfolioRetire,
  AdminPortfolioShow,
  AdminPortfolioUserMemberCreate,
  AdminPortfolioUserMemberEdit,
  AdminPortfolioUserMemberShow,
  AdminPrimaryBillingPointList,
  AdminPrimaryBillingPointShow,
  AdminPropertyList,
  AdminPropertyShow,
  AdminPropertyUserAdd,
  AdminPropertyUserList,
  AdminPropertyUserShow,
  AdminRetirePortfolioUserMember,
  AdminStandingChargeList,
  AdminStandingChargeShow,
  AdminTariffList,
  AdminTariffShow,
  AdminTradeRuleList,
  AdminTradeRuleShow,
  AdminUserInviteAdmin,
  AdminUserInviteCustomer,
  AdminUserList,
  AdminUserShow,
  AdminVolumeChargeList,
  AdminVolumeChargeShow,
} from 'src/components/Admin';
// Authentication
import {
  AuthConfirmEmail,
  AuthErrorPage,
  AuthForgotPassword,
  AuthLogin,
  AuthLogout,
  AuthRegister,
  AuthResetPassword,
} from 'src/components/Auth';
import HTTP404 from 'src/components/HTTP404';
// Layouts
import AdminLayout from 'src/components/Layout/AdminLayout';
import AnonymousLayout from 'src/components/Layout/AnonymousLayout';
import CustomerLayout from 'src/components/Layout/CustomerLayout';
import CustomerPortfolioLayout from 'src/components/Layout/CustomerLayout/CustomerPortfolioLayout';
import CustomerPropertyLayout from 'src/components/Layout/CustomerLayout/CustomerPropertyLayout';
// Loading
import Loading from 'src/components/Loading';
// Portfolio
import { PortfolioList } from 'src/components/Portfolio';
import PortfolioPropertyMemberList from 'src/components/Portfolio/PortfolioShow/PortfolioPropertyMemberList';
import PortfolioUserMemberList from 'src/components/Portfolio/PortfolioShow/PortfolioUserMemberList';
// Property
import { PropertySettings } from 'src/components/Property';
import { TariffList } from 'src/components/Tariff';
import {
  TradeRuleActive,
  TradeRuleCustomerProposals,
  TradeRuleList,
  TradeRulePrioritizeNominated,
  TradeRuleProposeNominated,
  TradeRuleSetCommunity,
  TradeRuleShow,
} from 'src/components/TradeRule';
// Config.
import { APIConfig } from 'src/config';
import {
  AUTH_ERROR_USER_DEACTIVATED, DEFAULT_LIST_PAGE_SIZE, INHIBIT_PORTFOLIO_VIEWS,
  TradeRuleStates, TradeTypes,
} from 'src/util/constants';
import { getAggregation, getRouteTimeRange } from 'src/util/time';

import AuthenticatedRoute from './AuthenticatedRoute';
import PortfolioShowRouteRenderer, { portfolioShowRouteQuery } from './PortfolioShowRoute';
import ProfileSettingsRouteRenderer, { profileSettingsRouteQuery } from './ProfileSettingsRoute';
import PropertyListRouteRenderer, { propertyListRouteQuery } from './PropertyListRoute';
import PropertyShowRouteRenderer, { propertyShowRouteQuery } from './PropertyShowRoute';
import TradeListRouteRenderer, { tradeListRouteQuery } from './TradeListRoute';
import TradeRuleCustomerShowRouteRenderer, { tradeRuleCustomerShowRouteQuery } from './TradeRuleCustomerShowRoute';
import TradeRuleProposalNominatedGeneralisedRouteRenderer, { tradeRuleProposalNominatedGeneralisedRouteQuery } from './TradeRuleProposalNominatedGeneralisedRoute';
import FlashProvider from '../components/Flashes/FlashesProvider';

export default makeRouteConfig(
  <Route Component={FlashProvider}>

    <Route path="/home" Component={AnonymousLayout} />

    <Route path="/confirm-email" Component={AnonymousLayout}>
      <Route path=":token" Component={AuthConfirmEmail} />
    </Route>

    <Route path="/login" Component={AnonymousLayout}>
      <Route Component={AuthLogin} />
    </Route>

    <Route path="/logout" Component={AnonymousLayout}>
      <Route Component={AuthLogout} />
    </Route>

    <Route path="/forgot-password" Component={AnonymousLayout}>
      <Route Component={AuthForgotPassword} />
    </Route>

    <Route path="/reset-password" Component={AnonymousLayout}>
      <Route path=":token" Component={AuthResetPassword} />
    </Route>

    <Route path="/register" Component={AnonymousLayout}>
      <Route path=":token" Component={AuthRegister} />
    </Route>

    <Route path="/auth/deactivated">
      <Route render={() => <AuthErrorPage errorType={AUTH_ERROR_USER_DEACTIVATED} />} />
    </Route>

    {/* TODO: admin layouts! */}
    <AuthenticatedRoute
      path="/admin"
      render={({ props, error }) => {
        const authErrorResult = handleApplicationErrors(props, error);
        if (authErrorResult) {
          return authErrorResult;
        }
        return <AdminLayout {...props} />;
      }}
      query={graphql`
        query routes_AdminRoot_Query {
          viewer {
            ...AdminLayout_viewer
          }
        }
      `}
    >
      <Redirect from="" to="/admin/properties" />
      <Route path="communities">
        <Route
          render={({ props }) => (props ? <AdminCommunityList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_AdminCommunityList_Query(
              $count: Int
              $cursor: String
            ) {
              viewer {
                ...AdminCommunityList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
          render={({ props }) => (props ? <AdminCommunityShow {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_AdminCommunityShow_Query(
              $uuid: ID!
              $count: Int
              $cursor: String
            ) {
              community(uuid: $uuid) {
                ...AdminCommunityShow_community
              }
            }
          `}
        />
      </Route>
      <Route path="meters">
        <Route
          render={({ props }) => (props ? <AdminMeterList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_AdminMeterList_Query($count: Int, $cursor: String) {
              viewer {
                ...AdminMeterList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
        >
          <Route
            render={({ props }) => (props ? <AdminMeterShow {...props} /> : <Loading />)}
            query={graphql`
              query routes_AdminMeterShow_Query($uuid: ID!) {
                meter(uuid: $uuid) {
                  ...AdminMeterShow_meter
                }
              }
            `}
          />
          <Route
            path="trade-rules"
            render={
              ({ props, variables }) => (
                props ? <AdminMeterTradeRules {...props} variables={variables} /> : <Loading />
              )
            }
            prepareVariables={(params, { location }) => {
              const {
                state, type, date, tz,
              } = location.query;

              const variables = { ...params, first: 50 };

              if (state && TradeRuleStates.includes(state)) {
                variables.state = state;
              }
              if (type && TradeTypes.includes(type)) {
                variables.type = type;
              }

              let timeZone = APIConfig().DEFAULT_TIMEZONE;
              if (tz && tz !== undefined) {
                timeZone = tz;
              }
              if (date) {
                const d = DateTime.fromISO(date, { zone: timeZone });
                if (d.isValid) {
                  variables.start = d.startOf('day').toSeconds();
                  variables.finish = d.startOf('day').plus({ days: 1 }).toSeconds();
                }
              }

              return variables;
            }}
            query={graphql`
              query routes_AdminMeterTradeRules_Query(
                $uuid: ID!
                $count: Int
                $cursor: String
                $state: TradeRuleState
                $type: TradeType
                $start: Timestamp
                $finish: Timestamp
              ) {
                meter(uuid: $uuid) {
                  ...AdminMeterTradeRules_meter
                }
              }
            `}
          />
        </Route>
      </Route>
      <Route path="portfolios">
        <Route
          render={({ props }) => (props ? <AdminPortfolioList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={graphql`
            query routes_AdminPortfolioList_Query($count: Int, $cursor: String) {
              viewer {
                ...AdminPortfolioList_viewer @arguments(count: $count, cursor: $cursor)
              }
            }
          `}
        />
        <Route
          path="new"
          render={({ props }) => (props ? <AdminPortfolioCreate {...props} /> : <Loading />)}
        />
        <Route path=":uuid">
          <Route
            render={({ props }) => (props ? <AdminPortfolioShow {...props} /> : <Loading />)}
            query={graphql`
            query routes_AdminPortfolioShow_Query($uuid: ID!) {
              portfolio(uuid: $uuid) {
                ...AdminPortfolioShow_portfolio
              }
            }
          `}
          />
          <Route
            path="edit"
            render={({ props }) => (props ? <AdminPortfolioEdit {...props} /> : <Loading />)}
            query={graphql`
            query routes_AdminPortfolioEdit_Query($uuid: ID!) {
              portfolio(uuid: $uuid) {
                ...AdminPortfolioEdit_portfolio
              }
            }
          `}
          />
          <Route
            path="retire"
            render={({ props }) => (props ? <AdminPortfolioRetire {...props} /> : <Loading />)}
            query={graphql`
            query routes_AdminPortfolioRetire_Query($uuid: ID!) {
              portfolio(uuid: $uuid) {
                ...AdminPortfolioRetire_portfolio
              }
            }
          `}
          />
          <Route path="portfolio-user-member">
            <Route
              path="invite"
              render={({ props }) => (props ? <AdminPortfolioUserMemberCreate {...props} />
                : <Loading />)}
              prepareVariables={(params) => ({ ...params, count: 500 })}
              query={graphql`
              query routes_AdminPortfolioUserMemberCreate_Query($uuid: ID!) {
              portfolio(uuid: $uuid) {
                ...AdminPortfolioUserMemberCreate_portfolio
              }
            }
            `}
            />
          </Route>
          <Route path="portfolio-property-member">
            <Route
              path="add"
              render={({ props }) => (props ? <AdminPortfolioPropertyMemberCreate {...props} />
                : <Loading />)}
              prepareVariables={(params) => ({ ...params, count: 50 })}
              query={graphql`
              query routes_AdminPortfolioPropertyMemberCreate_Query(
                $uuid: ID!
                $count: Int
                $cursor: String
                ) {
              viewer {
                ...AdminPortfolioPropertyMemberCreateProperties_viewer @arguments(
                  count: $count,
                  cursor: $cursor
                )
              }
              portfolio(uuid: $uuid) {
                ...AdminPortfolioPropertyMemberCreate_portfolio
              }
            }
            `}
            />
          </Route>
        </Route>
      </Route>
      <Route path="portfolio-property-members">
        <Route path=":uuid">
          <Route
            render={({ props }) => (props
              ? <AdminPortfolioPropertyMemberShow {...props} />
              : <Loading />)}
            query={graphql`
            query routes_AdminPortfolioPropertyMemberShow_Query($uuid: ID!) {
              portfolioPropertyMember(uuid: $uuid) {
                ...AdminPortfolioPropertyMemberShow_portfolioPropertyMember
              }
            }
          `}
          />
          <Route
            path="retire"
            render={({ props }) => (props
              ? <AdminPortfolioPropertyMemberRetire {...props} />
              : <Loading />)}
            query={graphql`
          query routes_AdminPortfolioPropertyMemberRetire_Query($uuid: ID!) {
            portfolioPropertyMember(uuid: $uuid) {
              ...AdminPortfolioPropertyMemberRetire_portfolioPropertyMember
            }
          }
        `}
          />
        </Route>
      </Route>
      <Route path="portfolio-user-members">
        <Route path=":uuid">
          <Route
            render={({ props }) => (props
              ? <AdminPortfolioUserMemberShow {...props} />
              : <Loading />)}
            query={graphql`
            query routes_AdminPortfolioUserMemberShow_Query($uuid: ID!) {
              portfolioUserMember(uuid: $uuid) {
                ...AdminPortfolioUserMemberShow_portfolioUserMember
              }
            }
          `}
          />
          <Route
            path="retire"
            render={({ props }) => (props
              ? <AdminRetirePortfolioUserMember {...props} />
              : <Loading />)}
            query={graphql`
            query routes_AdminRetirePortfolioUserMember_Query($uuid: ID!) {
              portfolioUserMember(uuid: $uuid) {
                ...AdminRetirePortfolioUserMember_portfolioUserMember
              }
            }
          `}
          />
          <Route
            path="edit"
            render={({ props }) => (props
              ? <AdminPortfolioUserMemberEdit {...props} />
              : <Loading />)}
            query={graphql`
            query routes_AdminPortfolioUserMemberEdit_Query($uuid: ID!) {
              portfolioUserMember(uuid: $uuid) {
                ...AdminPortfolioUserMemberEdit_portfolioUserMember
              }
            }
          `}
          />
        </Route>
      </Route>
      <Route path="primary-billing-points">
        <Route
          render={
            ({ props }) => (props ? <AdminPrimaryBillingPointList {...props} /> : <Loading />)
          }
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_AdminPrimaryBillingPointList_Query(
              $count: Int
              $cursor: String
            ) {
              viewer {
                ...AdminPrimaryBillingPointList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
          render={
            ({ props }) => (props ? <AdminPrimaryBillingPointShow {...props} /> : <Loading />)
          }
          query={graphql`
            query routes_AdminPrimaryBillingPointShow_Query($uuid: ID!) {
              primaryBillingPoint(uuid: $uuid) {
                ...AdminPrimaryBillingPointShow_primaryBillingPoint
              }
            }
          `}
        />
      </Route>
      <Route path="properties">
        <Route
          render={({ props }) => (props ? <AdminPropertyList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={graphql`
            query routes_AdminPropertyList_Query($count: Int, $cursor: String) {
              viewer {
                ...AdminPropertyList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
          render={({ props }) => (props ? <AdminPropertyShow {...props} /> : <Loading />)}
          query={graphql`
            query routes_AdminPropertyShow_Query($uuid: ID!) {
              property(uuid: $uuid) {
                ...AdminPropertyShow_property
              }
            }
          `}
        />
      </Route>
      <Route path="property-users">
        <Route
          render={({ props }) => (props ? <AdminPropertyUserList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_AdminPropertyUserList_Query(
              $count: Int
              $cursor: String
            ) {
              viewer {
                ...AdminPropertyUserList_viewer
              }
            }
          `}
        />
        <Route path="new">
          <Route
            path=""
            render={({ props }) => (props ? <AdminPropertyUserAdd {...props} /> : <Loading />)}
            prepareVariables={(params) => ({ ...params, count: 50 })}
            query={graphql`
              query routes_AdminPropertyUserAdd_Query(
                $count: Int
                $cursor: String
              ) {
                viewer {
                  ...AdminPropertyUserAdd_viewer @arguments(count: $count, cursor: $cursor)
                  ...AdminPropertyUserAddUsers_viewer @arguments(count: $count, cursor: $cursor)
                }
              }
            `}
          />
        </Route>
        <Route
          path=":uuid"
          render={({ props }) => (props ? <AdminPropertyUserShow {...props} /> : <Loading />)}
          query={graphql`
            query routes_AdminPropertyUserShow_Query($uuid: ID!) {
              propertyUser(uuid: $uuid) {
                ...AdminPropertyUserShow_propertyUser
              }
            }
          `}
        />
      </Route>
      <Route path="standing-charges">
        <Route
          render={({ props }) => (props ? <AdminStandingChargeList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_AdminStandingChargeList_Query(
              $count: Int
              $cursor: String
            ) {
              viewer {
                ...AdminStandingChargeList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
          render={({ props }) => (props ? <AdminStandingChargeShow {...props} /> : <Loading />)}
          query={graphql`
            query routes_AdminStandingChargeShow_Query($uuid: ID!) {
              standingCharge(uuid: $uuid) {
                ...AdminStandingChargeShow_standingCharge
              }
            }
          `}
        />
      </Route>
      <Route path="tariffs">
        <Route
          render={({ props }) => (props ? <AdminTariffList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_AdminTariffList_Query($count: Int, $cursor: String) {
              viewer {
                ...AdminTariffList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
          render={({ props }) => (props ? <AdminTariffShow {...props} /> : <Loading />)}
          query={graphql`
            query routes_AdminTariffShow_Query($uuid: ID!) {
              tariff(uuid: $uuid) {
                ...AdminTariffShow_tariff
              }
            }
          `}
        />
      </Route>
      <Route path="trade-rules">
        <Route
          render={
            ({ props, variables }) => (
              props ? <AdminTradeRuleList {...props} variables={variables} /> : <Loading />
            )
          }
          prepareVariables={(params, { location }) => {
            const {
              state, type, date, tz,
            } = location.query;
            let timeZone = APIConfig().DEFAULT_TIMEZONE;
            if (tz && tz !== undefined) {
              timeZone = tz;
            }

            const variables = { ...params, first: 50 };

            if (state && TradeRuleStates.includes(state)) {
              variables.state = state;
            }
            if (type && TradeTypes.includes(type)) {
              variables.type = type;
            }
            if (date) {
              const d = DateTime.fromISO(date, { zone: timeZone });
              if (d.isValid) {
                variables.start = d.startOf('day').toSeconds();
                variables.finish = d.startOf('day').plus({ days: 1 }).toSeconds();
              }
            }

            return variables;
          }}
          query={graphql`
            query routes_AdminTradeRuleList_Query(
              $count: Int
              $cursor: String
              $state: TradeRuleState
              $type: TradeType
              $start: Timestamp
              $finish: Timestamp
            ) {
              viewer {
                ...AdminTradeRuleList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
          render={({ props }) => (props ? <AdminTradeRuleShow {...props} /> : <Loading />)}
          query={graphql`
            query routes_AdminTradeRuleShow_Query($uuid: ID!) {
              rule(uuid: $uuid) {
                ...AdminTradeRuleShow_rule
              }
            }
          `}
        />
      </Route>
      <Route path="users">
        <Route
          render={({ props }) => (props ? <AdminUserList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_AdminUserList_Query($count: Int, $cursor: String) {
              viewer {
                ...AdminUserList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
          render={({ props }) => (props ? <AdminUserShow {...props} /> : <Loading />)}
          query={graphql`
            query routes_AdminUserShow_Query($uuid: ID!) {
              user(uuid: $uuid) {
                ...AdminUserShow_user
              }
            }
          `}
        />
        <Route path="invite">
          <Route
            path="admin"
            Component={AdminUserInviteAdmin}
          />
          <Route
            path="customer"
            Component={AdminUserInviteCustomer}
          />
        </Route>
      </Route>
      <Route path="volume-charges">
        <Route
          render={({ props }) => (props ? <AdminVolumeChargeList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_AdminVolumeChargeList_Query(
              $count: Int
              $cursor: String
            ) {
              viewer {
                ...AdminVolumeChargeList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
          render={({ props }) => (props ? <AdminVolumeChargeShow {...props} /> : <Loading />)}
          query={graphql`
            query routes_AdminVolumeChargeShow_Query($uuid: ID!) {
              volumeCharge(uuid: $uuid) {
                ...AdminVolumeChargeShow_volumeCharge
              }
            }
          `}
        />
      </Route>
    </AuthenticatedRoute>

    <AuthenticatedRoute
      path="/"
      Component={CustomerLayout}
      query={graphql`
        query routes_Root_Query {
          viewer {
            id viewerUser {
              uuid,
              type
            }
            ...CustomerLayout_viewer
          }
        }
      `}
      prepareVariables={(params, _others) => ({ ...params })}
      render={({ props, error }) => {
        const authErrorResult = handleApplicationErrors(props, error);
        if (authErrorResult) {
          return authErrorResult;
        }

        return <CustomerLayout {...props} />;
      }}
    >

      <Redirect from="" to="/properties" />

      <Route path="profile">
        <Redirect from="" to="/profile/settings" />
        <Route
          path="settings"
          query={profileSettingsRouteQuery}
          render={({ props, error }) => {
            const authErrorResult = handleApplicationErrors(props, error);
            if (authErrorResult) {
              return authErrorResult;
            }

            return <ProfileSettingsRouteRenderer routeProps={props} />;
          }}
        />
      </Route>

      <Route path="properties">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={propertyListRouteQuery}
          render={({ props, error }) => {
            const authErrorResult = handleApplicationErrors(props, error);
            if (authErrorResult) {
              return authErrorResult;
            }

            return <PropertyListRouteRenderer routeProps={props} />;
          }}
        />
      </Route>
      <Route path="portfolios">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={graphql`
            query routes_PortfolioList_Query($count: Int, $cursor: String) {
              viewer {
                ...PortfolioList_viewer @arguments(count: $count, cursor: $cursor)
              }
            }
          `}
          render={({ props }) => {
            const inhibitPortfolioViews = APIConfig().feature(INHIBIT_PORTFOLIO_VIEWS);

            if (!props) {
              return <Loading />;
            }

            if (props && inhibitPortfolioViews) {
              return <HTTP404 />;
            }

            return <PortfolioList {...props} />;
          }}
        />
      </Route>

      <Route path="trade-rules">
        <Route
          Component={TradeRuleCustomerProposals}
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={graphql`
            query routes_TradeRuleCustomerProposals_Query {
              viewer {
                ...TradeRuleCustomerProposals_viewer
              }
            }
          `}
          render={({ props }) => (props ? <TradeRuleCustomerProposals {...props} /> : <Loading />)}
        />

        <Route
          path=":uuid"
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={tradeRuleCustomerShowRouteQuery}
          render={({ props }) => (props ? (
            <TradeRuleCustomerShowRouteRenderer
              key={props.match.params.uuid}
              routeProps={props}
            />
          ) : <Loading />)}
        />
        <Route
          path="/propose/nominated"
        >
          <Route
            path=""
            query={tradeRuleProposalNominatedGeneralisedRouteQuery}
            render={({ props }) => (props ? (
              <TradeRuleProposalNominatedGeneralisedRouteRenderer routeProps={props} />
            ) : <Loading />)}
          />
        </Route>
      </Route>
    </AuthenticatedRoute>

    <AuthenticatedRoute
      path="properties/:uuid"
      Component={CustomerPropertyLayout}
      query={graphql`
        query routes_CustomerPropertyRoot_Query($uuid: ID!) {
          viewer {
            ...CustomerPropertyLayout_viewer
          }
          property(uuid: $uuid) {
            ...CustomerPropertyLayout_property
          }
        }
      `}
      prepareVariables={(params, _others) => ({ ...params })}
      render={({ props, error }) => {
        const authErrorResult = handleApplicationErrors(props, error);
        if (authErrorResult) {
          return authErrorResult;
        }
        return <CustomerPropertyLayout {...props} />;
      }}
    >
      <Route
        path=""
        query={propertyShowRouteQuery}
        prepareVariables={(params, { location }) => {
          const {
            start, finish, tz,
          } = location.query;

          let timeZone = APIConfig().DEFAULT_TIMEZONE;
          if (tz && tz !== undefined) {
            timeZone = tz;
          }

          const { s, f } = getRouteTimeRange(start, finish, timeZone);
          const agg = getAggregation(s, f);

          return {
            ...params,
            start: s.toSeconds(),
            finish: f.toSeconds(),
            timeZone,
            historianAggregation: agg,
            tradeAggregation: agg,
          };
        }}
        render={({ props }) => (props ? (
          <PropertyShowRouteRenderer key={props.match.params.uuid} routeProps={props} />
        ) : <Loading />)}
      />
      <Route
        path="settings"
        Component={PropertySettings}
        query={graphql`
          query routes_PropertySettings_Query($uuid: ID!) {
            property(uuid: $uuid) {
              ...PropertySettings_property
            }
          }
        `}
        render={({ props }) => (props ? <PropertySettings {...props} /> : <Loading />)}
      />
      <Route
        path="tariffs"
        Component={TariffList}
        query={graphql`
          query routes_TariffList_Query($uuid: ID!) {
            property(uuid: $uuid) {
              ...TariffList_property
            }
          }
        `}
        render={({ props }) => (props ? <TariffList {...props} /> : <Loading />)}
      />
      <Route path="trade-rules">
        <Route
          path="active"
          Component={TradeRuleActive}
          query={graphql`
            query routes_TradeRuleActive_Query($uuid: ID!) {
              property(uuid: $uuid) {
                ...TradeRuleActive_property
              }
            }
          `}
          render={({ props }) => (props ? <TradeRuleActive {...props} /> : <Loading />)}
        />
        <Route path="propose/nominated">
          <Route
            path=""
            Component={TradeRuleProposeNominated}
            prepareVariables={(params) => {
              const timezone = APIConfig().DEFAULT_TIMEZONE;

              return {
                ...params,
                now: Math.floor(DateTime.local({ zone: timezone }).toSeconds()),
              };
            }}
            query={graphql`
              query routes_TradeRuleProposeNominated_Query($uuid: ID!, $now: Timestamp!) {
                property(uuid: $uuid) {
                  ...TradeRuleProposeNominated_property
                }
              }
            `}
            render={
              ({ props }) => (
                props ? <TradeRuleProposeNominated {...props} /> : <Loading />
              )
            }
          />
        </Route>
        <Route
          path=""
          Component={TradeRuleList}
          query={graphql`
            query routes_TradeRuleList_Query($uuid: ID!) {
              property(uuid: $uuid) {
                ...TradeRuleList_property
              }
            }
          `}
          render={({ props }) => (props ? <TradeRuleList {...props} /> : <Loading />)}
        />
        <Route
          path=":ruleId"
          Component={TradeRuleShow}
          query={graphql`
            query routes_TradeRuleShow_Query($uuid: ID!, $ruleId: ID!) {
              property(uuid: $uuid) {
                ...TradeRuleShow_property
              }
              rule(uuid: $ruleId) {
                ...TradeRuleShow_rule
              }
            }
          `}
          render={({ props }) => (props ? <TradeRuleShow {...props} /> : <Loading />)}
        />
      </Route>
      <Route path="meters/:meterId/trade-rules">
        <Route
          path="nominated/prioritize"
          Component={TradeRulePrioritizeNominated}
          query={graphql`
            query routes_TradeRulePrioritizeNominated_Query($uuid: ID!, $meterId: ID!) {
              property(uuid: $uuid) {
                ...TradeRulePrioritizeNominated_property
              }
              meter(uuid: $meterId) {
                ...TradeRulePrioritizeNominated_meter
              }
            }
          `}
          render={
            ({ props }) => (props ? <TradeRulePrioritizeNominated {...props} /> : <Loading />)
          }
        />
        <Route path="community/set">
          <Route
            path=""
            Component={TradeRuleSetCommunity}
            prepareVariables={(params) => {
              const timezone = APIConfig().DEFAULT_TIMEZONE;
              return {
                ...params,
                now: Math.floor(DateTime.local({ zone: timezone }).toSeconds()),
              };
            }}
            query={graphql`
              query routes_TradeRuleSetCommunity_Query($uuid: ID!, $meterId: ID!, $now: Timestamp!) {
                property(uuid: $uuid) {
                  ...TradeRuleSetCommunity_property
                }
                meter(uuid: $meterId) {
                  ...TradeRuleSetCommunity_meter
                }
              }
            `}
            render={({ props }) => (props ? <TradeRuleSetCommunity {...props} /> : <Loading />)}
          />
        </Route>
      </Route>
      <Route path="trades">
        <Route
          path="history"
          query={tradeListRouteQuery}
          render={({ props }) => (props ? (
            <TradeListRouteRenderer key={props.match.params.uuid} routeProps={props} />
          ) : <Loading />)}
        />
      </Route>
    </AuthenticatedRoute>

    <AuthenticatedRoute
      path="portfolios/:uuid"
      Component={CustomerPortfolioLayout}
      query={graphql`
        query routes_CustomerPortfolioRoot_Query($uuid: ID!) {
          viewer {
            ...CustomerPortfolioLayout_viewer
          }
          portfolio(uuid: $uuid) {
            ...CustomerPortfolioLayout_portfolio
          }
        }
      `}
      prepareVariables={(params, _others) => ({ ...params })}
      render={({ props, error }) => {
        const authErrorResult = handleApplicationErrors(props, error);
        if (authErrorResult) {
          return authErrorResult;
        }
        return (
          <CustomerPortfolioLayout
            // Force layout remount when switching portfolios to ensure full state re-initialization
            key={props.match.params.uuid}
            {...props}
          />
        );
      }}
    >
      <Route
        path=""
        query={portfolioShowRouteQuery}
        prepareVariables={(params, { location }) => {
          const {
            start, tz,
          } = location.query;

          let timeZone = APIConfig().DEFAULT_TIMEZONE;
          if (tz && tz !== undefined) {
            timeZone = tz;
          }

          // Get original date values from URL if available
          let originalStart;

          if (start && start !== undefined) {
            const ts = DateTime.fromISO(start, { zone: timeZone });
            if (ts.invalidExplanation === null) {
              originalStart = ts.startOf('day');
            }
          }

          // FORCE a minimal time range to prevent AWS Lambda limits
          // We'll use that specified start date if available, otherwise use now
          const baseTime = originalStart
            ? originalStart.toSeconds()
            : Math.floor(DateTime.now().toSeconds());

          // This is important! Always return a minimal time range to avoid hitting Lambda limits
          // But preserve the ACTUAL selected date as the base point
          return {
            ...params,
            start: baseTime,
            finish: baseTime + 1, // Just 1 second range
            timeZone,
            historianAggregation: 'PT30M',
            tradeAggregation: 'PT30M',
          };
        }}
        render={({ props }) => {
          const inhibitPortfolioViews = APIConfig().feature(INHIBIT_PORTFOLIO_VIEWS);

          if (!props) {
            return <Loading />;
          }

          if (props && inhibitPortfolioViews) {
            return <HTTP404 />;
          }

          return (
            <PortfolioShowRouteRenderer
              key={props.match.params.uuid}
              routeProps={props}
            />
          );
        }}
      />
      <Route
        path="properties"
        Component={PortfolioPropertyMemberList}
        prepareVariables={(params, { location }) => {
          const { start, finish, tz } = location.query;
          let timeZone = APIConfig().DEFAULT_TIMEZONE;
          if (tz && tz !== undefined) {
            timeZone = tz;
          }

          const { s, f } = getRouteTimeRange(start, finish, timeZone);

          return {
            ...params,
            count: 100,
            start: s.toSeconds(),
            finish: f.toSeconds(),
          };
        }}
        query={graphql`
        query routes_PortfolioPropertyMemberList_Query(
          $uuid: ID!
          $count: Int,
          $cursor: String
          $start: Timestamp!
          $finish: Timestamp!
        ) {
          portfolio(uuid: $uuid){
            ...PortfolioPropertyMemberList_portfolio
              @arguments(count: $count, cursor: $cursor, start: $start, finish: $finish)
            }
        }
      `}
        render={({ props }) => (props ? <PortfolioPropertyMemberList {...props} /> : <Loading />)}
      />
      <Route
        path="users"
        Component={PortfolioUserMemberList}
        prepareVariables={(params, { location }) => {
          const { start, finish } = location.query;
          const timeZone = APIConfig().DEFAULT_TIMEZONE;

          const { s, f } = getRouteTimeRange(start, finish, timeZone);

          return {
            ...params,
            count: 100,
            start: s.toSeconds(),
            finish: f.toSeconds(),
          };
        }}
        query={graphql`
            query routes_PortfolioUserMembersList_Query(
              $uuid: ID!
              $count: Int,
              $cursor: String
              $start: Timestamp!
              $finish: Timestamp!
              ) {
                portfolio(uuid: $uuid) {
                ...PortfolioUserMemberList_portfolio @arguments(count: $count, cursor: $cursor, start: $start, finish: $finish)
              }
            }
        `}
        render={({ props }) => (props ? <PortfolioUserMemberList {...props} /> : <Loading />)}
      />
    </AuthenticatedRoute>
  </Route>,

);
